import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {useTranslation} from 'react-i18next'
import {useDispatch} from 'react-redux'
import {KTSVG} from 'app/utils/helpers'

import {
  ALL_CATEGORIES_ID,
  CatalogueNode,
  CatalogueCategory,
  CataloguePayload,
  normalizeCatalogue,
  filterCategories,
  nodeKey,
  getLeafKeys,
  getCheckState,
  getInitialSelectedKeys,
  buildCataloguePayload,
} from './catalogueModel'

import './style.scss'
import {serviceManagementAction} from '../../redux/ServiceManagementSlice'

interface B8akCatalogueModalProps {
  isShow: boolean
  onHide: () => void

  /**
   * Called only after addCategory API succeeds.
   * Parent should use it to refresh its service tree.
   * Do not call addCategory again in this callback.
   */
  onAddToTree?: (payload: CataloguePayload) => void
}

const ICON_BASE = '/assets/images/icons'

const ICONS = {
  close: `${ICON_BASE}/close-line.svg`,
  info: `${ICON_BASE}/info-circle-line.svg`,
  link: `${ICON_BASE}/link-line.svg`,
  chevronRight: `${ICON_BASE}/chevron-right-line.svg`,
  chevronDown: `${ICON_BASE}/chevron-down-line.svg`,
  check: `${ICON_BASE}/check-line.svg`,
  minus: `${ICON_BASE}/minus-line.svg`,
}

const B8akCatalogueModal: React.FC<B8akCatalogueModalProps> = ({isShow, onHide, onAddToTree}) => {
  const {t, i18n} = useTranslation()
  const dispatch = useDispatch()

  // --------------------------------------------------
  // State
  // --------------------------------------------------

  const [categories, setCategories] = useState<CatalogueCategory[]>([])

  const [selectedCategoryKey, setSelectedCategoryKey] = useState<string>(ALL_CATEGORIES_ID)

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set())

  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set())

  const [loading, setLoading] = useState(false)
  const [addingCategory, setAddingCategory] = useState(false)

  const [error, setError] = useState<string | null>(null)
  const [addError, setAddError] = useState<string | null>(null)

  const requestIdRef = useRef(0)
  const addRequestIdRef = useRef(0)
  const addingRef = useRef(false)

  const isArabic = i18n.language.startsWith('ar')

  const getName = (node: CatalogueNode): string =>
    isArabic ? node.nameAr || node.nameEn : node.nameEn || node.nameAr

  // --------------------------------------------------
  // Fetch catalogue once per modal opening
  // --------------------------------------------------

  useEffect(() => {
    if (!isShow) return

    const requestId = ++requestIdRef.current

    setLoading(true)
    setError(null)
    setAddError(null)
    setCategories([])
    setSelectedCategoryKey(ALL_CATEGORIES_ID)
    setSelectedKeys(new Set())
    setExpandedKeys(new Set())

    dispatch(
      serviceManagementAction.getB8akCatalogue({
        callback: (res: CatalogueNode[] | CatalogueNode | null, err: any) => {
          if (requestId !== requestIdRef.current) return

          setLoading(false)

          if (err) {
            setError(typeof err === 'string' ? err : err?.message || 'Failed to load catalogue.')
            setCategories([])
            return
          }

          const result = normalizeCatalogue(res)

          // Save catalogue data
          setCategories(result)

          // Initialize checked items from API isChecked
          setSelectedKeys(getInitialSelectedKeys(result))

          // Expand category roots by default
          setExpandedKeys(new Set(result.map((_, index) => nodeKey(`category-${index}`, []))))
        },
      })
    )

    return () => {
      requestIdRef.current += 1
    }
  }, [isShow, dispatch])

  // --------------------------------------------------
  // Invalidate Add callback when modal closes
  // --------------------------------------------------

  useEffect(() => {
    if (isShow) return

    addRequestIdRef.current += 1
    addingRef.current = false
    setAddingCategory(false)
  }, [isShow])

  // --------------------------------------------------
  // Category Filter - FE only
  // --------------------------------------------------

  const categoryOptions = useMemo(
    () =>
      categories.map((category, index) => ({
        value: String(index),
        id: category.id,
        name: getName(category),
      })),
    [categories, isArabic]
  )

  const visibleCategories = useMemo(
    () => filterCategories(categories, selectedCategoryKey),
    [categories, selectedCategoryKey]
  )

  // --------------------------------------------------
  // Expand / Collapse
  // --------------------------------------------------

  const toggleExpanded = (key: string) => {
    setExpandedKeys((current) => {
      const next = new Set(current)

      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }

      return next
    })
  }

  // --------------------------------------------------
  // Checkbox Selection
  // --------------------------------------------------

  const toggleSelection = (node: CatalogueNode, categoryKey: string, path: number[]) => {
    const leafKeys = getLeafKeys(node, categoryKey, path)

    setSelectedKeys((current) => {
      const next = new Set(current)

      const allSelected = leafKeys.every((key: string) => next.has(key))

      leafKeys.forEach((key: string) => {
        if (allSelected) {
          next.delete(key)
        } else {
          next.add(key)
        }
      })

      return next
    })
  }

  // --------------------------------------------------
  // Build payload from ALL categories
  // --------------------------------------------------

  const payload = useMemo(
    () => buildCataloguePayload(categories, selectedKeys),
    [categories, selectedKeys]
  )

  // --------------------------------------------------
  // Add Category API (Redux Saga)
  // --------------------------------------------------

  const handleAddToTree = () => {
    // An empty payload is allowed: unticking everything removes those services from the tree.
    if (addingRef.current || loading) {
      return
    }

    addingRef.current = true
    setAddingCategory(true)
    setAddError(null)

    const requestId = ++addRequestIdRef.current

    dispatch(
      serviceManagementAction.addCategory({
        data: payload,
        callback: (res: string | null, err: any) => {
          if (requestId !== addRequestIdRef.current) {
            return
          }

          addingRef.current = false
          setAddingCategory(false)

          if (err) {
            console.error('Add category failed:', err)

            setAddError(
              typeof err === 'string'
                ? err
                : err?.message || 'Failed to add category. Please try again.'
            )
            return
          }

          console.log('Add category successfully:', res)

          // Parent refreshes its service tree.
          // Must NOT dispatch addCategory again.
          onAddToTree?.(payload)

          // Close only when API succeeds.
          onHide()
        },
      })
    )
  }

  // --------------------------------------------------
  // Render Tree
  // --------------------------------------------------

  const renderRows = (
    nodes: CatalogueNode[],
    categoryKey: string,
    parentPath: number[],
    depth: number
  ): React.ReactNode[] => {
    const rows: React.ReactNode[] = []

    nodes.forEach((node, index) => {
      const path = depth === 0 ? parentPath : [...parentPath, index]

      const key = nodeKey(categoryKey, path)

      const children = node.children || []
      const hasChildren = children.length > 0
      const isExpanded = expandedKeys.has(key)

      const checkState = getCheckState(node, categoryKey, path, selectedKeys)

      rows.push(
        <div
          key={key}
          className={`b8ak-catalogue__row${depth === 0 ? ' b8ak-catalogue__row--category' : ''}`}
          style={{
            paddingInlineStart: `${12 + depth * 24}px`,
          }}
        >
          {/* Expand / Collapse */}
          {hasChildren ? (
            <button
              type='button'
              className={`b8ak-catalogue__chevron${
                isExpanded ? '' : ' b8ak-catalogue__chevron--collapsed'
              }`}
              aria-expanded={isExpanded}
              onClick={() => toggleExpanded(key)}
            >
              <img
                src={isExpanded ? ICONS.chevronDown : ICONS.chevronRight}
                alt=''
                width={16}
                height={16}
              />
            </button>
          ) : (
            <span className='b8ak-catalogue__chevron-spacer' />
          )}

          {/* Checkbox and Name */}
          <button
            type='button'
            className='b8ak-catalogue__name-button'
            role='checkbox'
            aria-checked={checkState === 'indeterminate' ? 'mixed' : checkState === 'checked'}
            disabled={addingCategory}
            onClick={() => toggleSelection(node, categoryKey, path)}
          >
            <span
              className={`b8ak-catalogue__checkbox b8ak-catalogue__checkbox--${checkState}`}
              aria-hidden='true'
            >
              {checkState !== 'unchecked' && (
                <KTSVG
                  defaultColor={true}
                  path={checkState === 'checked' ? ICONS.check : ICONS.minus}
                />
              )}
            </span>

            <span className='b8ak-catalogue__name-en'>
              {getName(node)}
            </span>
            {node.isChecked === true && depth > 0 && (
              <span className='b8ak-catalogue__added-tag'>
                {t('SERVICE_MANAGEMENT_PAGE.CATALOGUE_MODAL.ADDED_TAG')}
              </span>
            )}

            {(node.duration != null || node.price != null) && (
              <span className='b8ak-catalogue__meta'>
                {[
                  node.duration != null &&
                    t('SERVICE_MANAGEMENT_PAGE.CATALOGUE_MODAL.SERVICE_DURATION', {
                      minutes: node.duration,
                    }),
                  node.price != null &&
                    t('SERVICE_MANAGEMENT_PAGE.CATALOGUE_MODAL.SERVICE_PRICE', {
                      price: node.price,
                    }),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            )}
          </button>
        </div>
      )

      if (hasChildren && isExpanded) {
        rows.push(...renderRows(children, categoryKey, path, depth + 1))
      }
    })

    return rows
  }

  // --------------------------------------------------
  // JSX
  // --------------------------------------------------

  return (
    <Modal
      show={isShow}
      onHide={onHide}
      backdrop='static'
      keyboard={false}
      centered
      dialogClassName='b8ak-catalogue__dialog'
      contentClassName='b8ak-catalogue__content'
    >
      <div className='b8ak-catalogue'>
        {/* Header */}
        <div className='b8ak-catalogue__header'>
          <h2 className='b8ak-catalogue__title'>
            {t('SERVICE_MANAGEMENT_PAGE.CATALOGUE_MODAL.TITLE')}
          </h2>

          <button
            type='button'
            className='b8ak-catalogue__close'
            disabled={addingCategory}
            onClick={onHide}
          >
            <KTSVG defaultColor={true} path={ICONS.close} />
          </button>
        </div>

        {/* Body */}
        <div className='b8ak-catalogue__body'>
          {/* Information Banner */}
          <div className='b8ak-catalogue__banner'>
            <span className='b8ak-catalogue__banner-icon'>
              <KTSVG defaultColor={true} path={ICONS.info} />
            </span>

            <p className='b8ak-catalogue__banner-text'>
              {t('SERVICE_MANAGEMENT_PAGE.CATALOGUE_MODAL.MARKETPLACE_NOTICE')}
            </p>
          </div>

          {/* Category Filter */}
          <div className='b8ak-catalogue__filter'>
            <label className='b8ak-catalogue__filter-label' htmlFor='b8ak-catalogue-category'>
              {isArabic ? 'الفئة' : 'Category'}
            </label>

            <select
              id='b8ak-catalogue-category'
              className='b8ak-catalogue__select'
              value={selectedCategoryKey}
              disabled={loading || addingCategory}
              onChange={(event) => setSelectedCategoryKey(event.target.value)}
            >
              <option value={ALL_CATEGORIES_ID}>
                {isArabic ? 'جميع الفئات' : 'All Categories'}
              </option>

              {categoryOptions.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Catalogue Tree */}
          <div className='b8ak-catalogue__tree'>
            {loading ? (
              <div className='text-center py-5'>
                <span className='spinner-border spinner-border-sm' role='status' />
              </div>
            ) : error ? (
              <div className='text-danger text-center py-4'>{error}</div>
            ) : visibleCategories.length === 0 ? (
              <div className='text-center py-4'>
                {isArabic ? 'لا توجد خدمات متاحة' : 'No services available'}
              </div>
            ) : (
              visibleCategories.map((category: any) => {
                const categoryIndex = categories.indexOf(category)

                const categoryKey = `category-${categoryIndex}`

                return (
                  <React.Fragment key={categoryKey}>
                    {renderRows([category], categoryKey, [], 0)}
                  </React.Fragment>
                )
              })
            )}
          </div>

          {/* Note */}
          <div className='b8ak-catalogue__orders-note'>
            <span className='b8ak-catalogue__orders-note-icon'>
              <KTSVG defaultColor={true} path={ICONS.link} />
            </span>

            {t('SERVICE_MANAGEMENT_PAGE.CATALOGUE_MODAL.ORDERS_NOTE')}
          </div>
        </div>

        {/* Add API Error */}
        {addError && (
          <div className='alert alert-danger mx-4 mb-3' role='alert'>
            {addError}
          </div>
        )}

        {/* Footer */}
        <div className='b8ak-catalogue__footer'>
          <button
            type='button'
            className='b8ak-catalogue__btn-cancel'
            disabled={addingCategory}
            onClick={onHide}
          >
            {t('SERVICE_MANAGEMENT_PAGE.CATALOGUE_MODAL.CANCEL')}
          </button>

          <span className='b8ak-catalogue__count'>
            {selectedKeys.size} {isArabic ? 'محدد' : 'selected'}
          </span>

          <button
            type='button'
            className='b8ak-catalogue__btn-primary'
            disabled={addingCategory || loading}
            onClick={handleAddToTree}
          >
            {addingCategory ? (
              <>
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                />
                {isArabic ? 'جارٍ الإرسال...' : 'Submitting...'}
              </>
            ) : (
              t('SERVICE_MANAGEMENT_PAGE.CATALOGUE_MODAL.SUBMIT')
            )}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default B8akCatalogueModal
